import { useEffect, useState } from 'react';
import ProgressBarLogic from '../../common/ProgressBarLogic';
import InputBox from '../../common/InputBox';
import Button from '../../common/Button';
import ToastNotification from '../../common/ToastNotification';
import Lopa from '../../assets/LopaaLogo.png';
import { useNavigate, useParams } from 'react-router-dom';
import { resetPassword } from '../../api/Auth';
import EyeButton from '../../common/EyeButton';

interface FormData {
  password: string;
  confirmPassword: string;
}

interface ValidationErrors {
  password: string;
  confirmPassword: string;
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: ''
  });
  const { token } = useParams<{ token?: string }>();
  const [errors, setErrors] = useState<ValidationErrors>({
    password: '',
    confirmPassword: ''
  });

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [password, setPassword] = useState({ password: '' });
  const [score, setScore] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formValid, setFormValid] = useState<boolean>(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    let isValid = true;
    const newErrors: ValidationErrors = {
      password: '',
      confirmPassword: ''
    };

    ['password', 'confirmPassword'].forEach((field) => {
      const value = formData[field as keyof FormData];
      let error = '';

      if (field === 'password') {
        if (!value.trim()) {
          newErrors.password = 'Password is required';
          isValid = false;
        } else if (score < 90) {
          newErrors.password = 'Password is not strong enough. Please create a stronger password.';
          isValid = false;
        }
      } else if (field === 'confirmPassword') {
        if (!value.trim()) {
          newErrors.confirmPassword = 'ConfirmPassword is required';
          isValid = false;
        } else if (value !== formData.password) {
          newErrors.confirmPassword = 'Passwords do not match';
          isValid = false;
        }
      }

      // newErrors[field as keyof ValidationErrors] = error;
      // if (error) {
      //   isValid = false;
      //   setFormData({
      //     password: '',
      //     confirmPassword: ''
      //   });
      // }
    });

    setErrors(newErrors);

    if (isValid) {
      setFormValid(true);
      try {
        const response = await resetPassword(token ?? '', {
          password: formData.password || ''
        });
        if (response.ok) {
          const data = await response.json();
          setScore(0);
          ToastNotification({
            message: data.message
          });

          setTimeout(() => {
            navigate('/login');
            setFormData({
              password: '',
              confirmPassword: ''
            });
            setFormValid(false);
          }, 1500);
        } else {
          const errorData = await response.json();
          ToastNotification({
            message: errorData.message,
            type: 'warning'
          });
          setFormValid(false);
        }
      } catch (error: any) { }
    }
  };

  useEffect(() => {
    const validateForm = () => {
      const isValid = !!formData.password && !!formData.confirmPassword && score >= 90;
      setIsFormValid(isValid);
    };

    validateForm();
  }, [formData, score]);

  return (
    <>
      <img
        src={Lopa}
        className="lg:w-32 lg:h-[60px] w-[101px] h-12 mr-2 ml-8 mt-8"
        alt="Matrix AI"
      />
      <div className="min-h-full flex flex-col justify-center px-2 py-3 lg:px-8 ">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg md:mt-18">
          <h2 className="mt-5 text-start text-base md:text-[36px] font-bold tracking-tight md:leading-[56.25px]">
            Reset Password
          </h2>
        </div>

        <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-lg">
          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <div className="mt-2 relative">
                <InputBox
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  placeholder="Enter new password"
                  handleChange={(e: any) => {
                    handleChange(e);
                    setPassword(e.target.value);
                  }}
                  error={errors.password}
                />
                <EyeButton
                  showPassword={showPassword}
                  togglePasswordVisibility={togglePasswordVisibility}
                />
              </div>

              <div className="h-[16px] block">
                {formData.password.length > 0 && (
                  <ProgressBarLogic password={password} setScore={setScore} score={score} />
                )}
              </div>
            </div>

            <div>
              <div className="mt-3 relative">
                <InputBox
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  placeholder="Confirm new password"
                  handleChange={handleChange}
                  error={errors.confirmPassword}
                />
                <EyeButton
                  showPassword={showConfirmPassword}
                  togglePasswordVisibility={toggleConfirmPasswordVisibility}
                />
              </div>
            </div>

            <div className="flex place-content-center">
              <Button
                isFormValid={isFormValid}
                btnText="Reset"
                textColor="#FFFFFF"
                backGroundColor="#006BEA"
                borderColor=""
                disable={formValid}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
